/**
 * @fileoverview User Management page component
 * @description Main page for managing users, doctors, pharmacists, and institution responsibles
 */
import React, { useRef, useState, useCallback } from "react";
import { DmsPage, DmsTabber, DmsAltBox, intl, DmsButton, DmsModal, DmsTextField, fetchData, getToken } from "dmsbasereact";
import { PasswordValidation } from "../../components/PasswordValidation/PasswordValidation";
import { Typography, Grid } from "@mui/material";
import Swal from "sweetalert2";
import { PatientTable } from "../../components/PatientTable/PatientTable";
import { UserTable } from "../../components/UserTable";
import { UserDetailModal } from "../../components/UserDetailModal";
import { UserFormModal } from "../../components/UserFormModal";
import { ModalUserBlockReason } from "../../components/ModalUserBlockReason";
import { useUserManagement } from "../../hooks/useUserManagement";

/**
 * User Management page component
 * @returns {JSX.Element} The page component
 */
export const UserManagementPage = () => {
  const tableSsoRef = useRef(null);
  const tableAllRef = useRef(null);
  const tablePersonRef = useRef(null);
  const tableDistributorResponsibleRef = useRef(null);
  const tablePharmacistRef = useRef(null);
  const tableDoctorRef = useRef(null);
  const tableAuditorRef = useRef(null);

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedUserDetail, setSelectedUserDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selectedUserType, setSelectedUserType] = useState(null);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createUserType, setCreateUserType] = useState(null);

  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [userToBlock, setUserToBlock] = useState(null);

  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [userToChangePassword, setUserToChangePassword] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordValid, setPasswordValid] = useState(false);

  const {
    availableRoles,
    loadingRoles,
    createUser,
    updateUserFull,
    fetchUserDetails,
    unblockUser,
  } = useUserManagement();

  const updateAllTables = useCallback(() => {
    tableSsoRef.current?.updateTable();
    tableAllRef.current?.updateTable();
    tablePersonRef.current?.updateTable();
    tableDistributorResponsibleRef.current?.updateTable();
    tablePharmacistRef.current?.updateTable();
    tableDoctorRef.current?.updateTable();
    tableAuditorRef.current?.updateTable();
  }, []);

  const handleViewDetails = useCallback(async (row, userType) => {
    setLoadingDetail(true);
    setDetailModalOpen(true);
    setSelectedUserType(userType);
    const details = await fetchUserDetails(row.pk);
    if (details) {
      setSelectedUserDetail(details);
    } else {
      setDetailModalOpen(false);
    }
    setLoadingDetail(false);
  }, [fetchUserDetails]);

  const handleCloseDetailModal = useCallback(() => {
    setDetailModalOpen(false);
    setSelectedUserDetail(null);
    setSelectedUserType(null);
  }, []);

  const handleSaveDetailEdit = useCallback(async (userPk, payload) => {
    const result = await updateUserFull(userPk, payload);
    if (result) {
      setSelectedUserDetail(result);
      updateAllTables();
    }
    return result;
  }, [updateUserFull, updateAllTables]);

  const handleOpenCreateModal = useCallback((userType = null) => {
    setCreateUserType(userType);
    setCreateModalOpen(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setCreateModalOpen(false);
    setCreateUserType(null);
  }, []);

  const handleCreateSave = useCallback(async (payload) => {
    if (!payload.user_login || !payload.user_name) {
      Swal.fire({
        icon: "warning",
        title: intl.get("MissingInformation").d("Missing Information"),
        text: intl.get("LoginAndNameRequired").d("Login and Name are required."),
      });
      return;
    }
    const success = await createUser(payload);
    if (success) {
      updateAllTables();
      handleCloseCreateModal();
    }
  }, [createUser, updateAllTables, handleCloseCreateModal]);

  const handleBlockUser = useCallback((row) => {
    setUserToBlock(row);
    setBlockModalOpen(true);
  }, []);

  const handleCloseBlockModal = useCallback(() => {
    setBlockModalOpen(false);
    setUserToBlock(null);
  }, []);

  const handleBlockSuccess = useCallback(() => {
    updateAllTables();
    handleCloseBlockModal();
  }, [updateAllTables, handleCloseBlockModal]);

  const handleUnblockUser = useCallback(async (row) => {
    const success = await unblockUser(row);
    if (success) {
      updateAllTables();
    }
  }, [unblockUser, updateAllTables]);

  const handleOpenPasswordModal = useCallback((row) => {
    setUserToChangePassword(row);
    setNewPassword("");
    setConfirmPassword("");
    setPasswordModalOpen(true);
  }, []);

  const handleClosePasswordModal = useCallback(() => {
    setPasswordModalOpen(false);
    setUserToChangePassword(null);
    setNewPassword("");
    setConfirmPassword("");
    setPasswordValid(false);
  }, []);

  const handlePasswordSubmit = useCallback(async () => {
    if (newPassword !== confirmPassword) {
      Swal.fire({
        icon: "error",
        title: intl.get("FailedOnPasswordConfirmation").d("Failed on password confirmation!"),
        customClass: { container: "swal-overlay" },
        didOpen: () => {
          document.querySelector(".swal-overlay").style.zIndex = 9999;
        },
      });
      return;
    }

    try {
      const token = getToken();
      const data = {
        user_password: newPassword,
        user_login: userToChangePassword?.user_login,
      };
      const url = `/user/password/${userToChangePassword?.pk}`;
      const response = await fetchData(url, "PUT", token.access_token, data);

      if (response?.status !== 200) {
        Swal.fire({
          icon: "error",
          title: response?.data?.detail || intl.get("Error").d("Error"),
        });
      } else {
        Swal.fire({
          icon: "success",
          title: response?.data?.detail || intl.get("PasswordChangedSuccessfully").d("Password changed successfully"),
        });
        handleClosePasswordModal();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: intl.get("ErrorPostingData").d("Error posting data"),
      });
    }
  }, [newPassword, confirmPassword, userToChangePassword, handleClosePasswordModal]);

  const getCommonTableProps = useCallback((userType = null) => ({
    onViewDetails: (row) => handleViewDetails(row, userType),
    onBlock: handleBlockUser,
    onUnblock: handleUnblockUser,
    onCreate: () => handleOpenCreateModal(userType),
  }), [handleViewDetails, handleBlockUser, handleUnblockUser, handleOpenCreateModal]);

  const tabs = [
    {
      title: intl.get("SsoUsers").d("SSO"),
      component: (
        <UserTable
          ref={tableSsoRef}
          userType="sso"
          {...getCommonTableProps("sso")}
        />
      ),
    },
    {
      title: intl.get("Users").d("Users"),
      component: (
        <UserTable
          ref={tableAllRef}
          onViewDetails={(row) => handleViewDetails(row, null)}
          onBlock={handleBlockUser}
          onUnblock={handleUnblockUser}
          onChangePassword={handleOpenPasswordModal}
          // onCreate intentionally omitted to hide create button
        />
      ),
    },
    {
      title: intl.get("InstitutionResponsibles").d("Institution Responsibles"),
      component: (
        <UserTable
          ref={tablePersonRef}
          userType="person"
          {...getCommonTableProps("person")}
          onChangePassword={handleOpenPasswordModal}
          showStatusFilter={true}
        />
      ),
    },
    {
      title: intl.get("DistributorResponsibles").d("Distributor Responsibles"),
      component: (
        <UserTable
          ref={tableDistributorResponsibleRef}
          userType="distributor_responsible"
          {...getCommonTableProps("distributor_responsible")}
          onChangePassword={handleOpenPasswordModal}
          showStatusFilter={true}
        />
      ),
    },
    {
      title: intl.get("Dispensers").d("Dispensadores"),
      component: (
        <UserTable
          ref={tablePharmacistRef}
          userType="pharmacist"
          {...getCommonTableProps("pharmacist")}
          onChangePassword={handleOpenPasswordModal}
          showStatusFilter={true}
        />
      ),
    },
    {
      title: intl.get("Prescribers").d("Prescritores"),
      component: (
        <UserTable
          ref={tableDoctorRef}
          userType="doctor"
          {...getCommonTableProps("doctor")}
          onChangePassword={handleOpenPasswordModal}
          showStatusFilter={true}
        />
      ),
    },
    {
      title: intl.get("Patients").d("Patients"),
      component: <PatientTable />,
    },
    {
      title: intl.get("Auditors").d("Auditores"),
      component: (
        <UserTable
          ref={tableAuditorRef}
          userType="auditor"
          {...getCommonTableProps("auditor")}
          onChangePassword={handleOpenPasswordModal}
        />
      ),
    },
  ];

  return (
    <DmsPage name={intl.get("UserManagement").d("User Management")} manual_slug="user-management">
      <DmsAltBox.Card>
        <DmsAltBox.Header>
          <Typography variant="h3" sx={{ fontSize: "1.1rem" }}>
            {intl.get("UserManagement").d("User Management")}
          </Typography>
        </DmsAltBox.Header>
        <DmsAltBox.Body>
          <DmsTabber tabs={tabs} pageName={intl.get("UserManagement").d("User Management")} />
        </DmsAltBox.Body>
      </DmsAltBox.Card>

      <UserDetailModal
        open={detailModalOpen}
        onClose={handleCloseDetailModal}
        user={selectedUserDetail}
        loading={loadingDetail}
        availableRoles={availableRoles}
        onSave={handleSaveDetailEdit}
        onUserUpdated={updateAllTables}
        userType={selectedUserType}
      />

      <UserFormModal
        isVisible={createModalOpen}
        onClose={handleCloseCreateModal}
        onSave={handleCreateSave}
        availableRoles={availableRoles}
        loadingRoles={loadingRoles}
        userType={createUserType}
      />

      <ModalUserBlockReason
        isVisible={blockModalOpen}
        onClose={handleCloseBlockModal}
        userData={userToBlock}
        onSuccess={handleBlockSuccess}
      />

      <DmsModal
        isVisible={passwordModalOpen}
        onClose={handleClosePasswordModal}
      >
        <DmsModal.Header
          title={`${intl.get("Changepassword").d("Change password")} - ${userToChangePassword?.user_name}`}
          onClose={handleClosePasswordModal}
        />
        <DmsModal.Body>
          <Grid container spacing={3} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <DmsTextField
                label={intl.get("NewPassword").d("New Password")}
                type="password"
                fullWidth
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <DmsTextField
                label={intl.get("ConfirmNewPassword").d("Confirm new password")}
                type="password"
                fullWidth
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <PasswordValidation
                password={newPassword}
                confirmPassword={confirmPassword}
                onValidChange={setPasswordValid}
              />
            </Grid>
          </Grid>
        </DmsModal.Body>
        <DmsModal.Footer>
          <DmsButton
            variant="secondary"
            text={intl.get("Cancel").d("Cancel")}
            onClick={handleClosePasswordModal}
          />
          <DmsButton
            variant="success"
            text={intl.get("Confirm").d("Confirm")}
            onClick={handlePasswordSubmit}
            disabled={!passwordValid}
          />
        </DmsModal.Footer>
      </DmsModal>
    </DmsPage>
  );
};
